import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '../services/api/auth';
import { api } from '../services/api/client';
import { useUser } from './useUser';
import { isEmail, required } from '../utils/validators';

type LoginFieldErrors = {
  email: string[];
  password: string[];
};

export const useLogin = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<LoginFieldErrors>({
    email: [],
    password: [],
  });
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const { setUser } = useUser();

  const validate = () => {
    const nextFieldErrors: LoginFieldErrors = {
      email: [],
      password: [],
    };
    const nextFormErrors: string[] = [];

    const emailRequiredError = required(email);
    if (emailRequiredError) {
      nextFieldErrors.email.push(emailRequiredError);
      nextFormErrors.push(emailRequiredError);
    } else {
      const emailError = isEmail(email);
      if (emailError) {
        nextFieldErrors.email.push(emailError);
        nextFormErrors.push(emailError);
      }
    }

    const passwordError = required(password);
    if (passwordError) {
      nextFieldErrors.password.push(passwordError);
      nextFormErrors.push(passwordError);
    }

    setFieldErrors(nextFieldErrors);
    setFormErrors(nextFormErrors);
    setError('');

    return nextFormErrors.length === 0;
  };

  const mutation = useMutation({
    mutationFn: () => authApi.login(email, password),
    onSuccess: (data) => {
      setError('');
      setFieldErrors({ email: [], password: [] });
      setFormErrors([]);
      api.setToken(data.token);
      setUser(data.user);

      const user = data.user;
      if (user.role === 'admin' || user.role === 'manager' || user.role === 'owner') {
        window.location.href = '/admin';
      } else {
        window.location.href = '/dashboard';
      }
    },
    onError: () => {
      const messageKey = 'auth.login.error';
      setError(t(messageKey));
      setFormErrors([messageKey]);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    mutation.mutate();
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    fieldErrors,
    formErrors,
    validate,
    handleSubmit,
    loading: mutation.isPending,
    error,
  };
};
